import React from "react";
import { SiteNav, SiteStickyFooter } from "./SiteChrome.js";
import SiteHero from "./SiteHero.js";
import SiteBackground from "./SiteBackground.js";
import { AD_MODE } from "../lib/site-config.js";

const FEATURED_GAME = {
  name: "Stack Wars",
  blurb: "Drop your pieces, lock in combos, and send your opponent spiraling into chaos.",
  icon: "🏗️",
  href: "/stack-wars",
  num: "01",
};

const MINI_GAMES = [
  { name: "Tic-Tac-Toe",     blurb: "The classic 3-in-a-row duel — solo or vs friend.", icon: "⭕", href: "/tic-tac-toe",  accent: "#ff4d8d", num: "02" },
  { name: "Memory Match",    blurb: "Flip cards, find pairs, test your recall.",          icon: "🃏", href: "/memory-match", accent: "#a855f7", num: "03" },
  { name: "Ladoo",           blurb: "Roll dice, race tokens home, capture rivals.",       icon: "🎲", href: "/ladoo",        accent: "#ffd44d", num: "04" },
  { name: "Battleship Wars", blurb: "Place your fleet, call shots, sink the enemy.",     icon: "⚓", href: "/battleship",   accent: "#00b4ff", num: "05" },
];

const TICKER_ITEMS = [
  "Stack Wars", "Tic-Tac-Toe", "Memory Match", "Ladoo", "Battleship Wars",
];

const CREW = [
  { emoji: "😤", name: "Spark", bg: "linear-gradient(160deg,#ffe57f,#ff9f44)", glow: "#ffb347" },
  { emoji: "⚡",  name: "Bolt",  bg: "linear-gradient(160deg,#93ffcf,#26c9a1)", glow: "#38f2b8" },
  { emoji: "🫧",  name: "Bloop", bg: "linear-gradient(160deg,#9fbcff,#7a76ff)", glow: "#a855f7" },
];

export default function MisterGamesPage() {
  return (
    <>
      <SiteBackground />

      <div className="site-wrap">
        <SiteNav />

        <SiteHero
          title="Simple Games, Big Fun"
          subtitle="No downloads. No rules. Just pure browser-based fun. Smack talk the competition."
          ctaLabel="Play Stack Wars"
          ctaHref="/stack-wars"
          ctaSecondaryLabel="All Games"
          ctaSecondaryHref="/#games"
        />

        {/* ── Game Select ──────────────────────────────────────────── */}
        <section className="game-select" id="games">

          {/* Scrolling ticker */}
          <div className="ticker-strip" aria-hidden="true">
            <div className="ticker-inner">
              {Array.from({ length: 8 }).map((_, rep) =>
                TICKER_ITEMS.map((name) => (
                  <React.Fragment key={`${rep}-${name}`}>
                    <span className="ticker-item">{name}</span>
                    <span className="ticker-sep">★</span>
                  </React.Fragment>
                ))
              )}
            </div>
          </div>

          {/* Section header */}
          <div className="gs-header">
            <div>
              <p className="gs-eyebrow">// Select Your Stage</p>
              <h2 className="gs-title">
                Choose Your<span className="gs-title-accent"> Game</span>
                <span className="gs-cursor" aria-hidden="true" />
              </h2>
            </div>
            <div className="gs-subline">5 games live · no account needed</div>
          </div>

          {/* Grid: 1 featured + 4 mini */}
          <div className="gs-grid">

            {/* Featured card */}
            <article className="feat-card">
              <div className="feat-card-glow" />
              <div className="feat-card-diag" />
              <span className="feat-card-num">{FEATURED_GAME.num}</span>

              <div className="feat-card-icon-area">
                <div className="feat-card-icon-circle">
                  {FEATURED_GAME.icon}
                </div>
              </div>

              <div className="feat-card-label">Featured</div>
              <h3 className="feat-card-name">{FEATURED_GAME.name}</h3>
              <p className="feat-card-blurb">{FEATURED_GAME.blurb}</p>
              <a className="feat-card-cta" href={FEATURED_GAME.href}>▶ Play Now</a>
            </article>

            {/* Mini cards */}
            {MINI_GAMES.map((g) => (
              <article
                key={g.name}
                className="mini-card"
                style={{
                  "--mini-accent":        g.accent,
                  "--mini-accent-bg":     `${g.accent}1e`,
                  "--mini-accent-shadow": `${g.accent}66`,
                } as React.CSSProperties}
              >
                <div className="mini-card-accent-bar" style={{ background: g.accent }} />
                <span className="mini-card-num">{g.num}</span>
                <div
                  className="mini-card-icon"
                  style={{
                    border: `1px solid ${g.accent}45`,
                    boxShadow: `0 0 24px ${g.accent}28, inset 0 0 24px ${g.accent}0a`,
                    background: `radial-gradient(circle at center, ${g.accent}18 0%, ${g.accent}06 55%, transparent 80%)`,
                  }}
                >
                  {g.icon}
                </div>
                <h3 className="mini-card-name">{g.name}</h3>
                <p className="mini-card-blurb">{g.blurb}</p>
                <a className="mini-card-cta" href={g.href}>▶ Play</a>
              </article>
            ))}

          </div>
        </section>

        {/* ── Meet the Crew ────────────────────────────────────────── */}
        <section className="character-panel" id="crew">
          <p className="gs-eyebrow">// The Crew</p>
          <h2>Meet the Characters</h2>
          <p>Spark, Bolt, and Bloop — your chaotic guides to MisterGames.</p>

          <div className="crew-emoji-row">
            {CREW.map((c) => (
              <div
                key={c.name}
                className="crew-emoji-box"
                style={{
                  background: c.bg,
                  border: `2px solid ${c.glow}`,
                  boxShadow: `0 0 30px ${c.glow}60`,
                }}
              >
                {c.emoji}
              </div>
            ))}
          </div>

          <div className="crew-names">
            {CREW.map((c) => (
              <div key={c.name} className="crew-name">{c.name}</div>
            ))}
          </div>
        </section>

        <SiteStickyFooter adMode={AD_MODE} />
      </div>
    </>
  );
}
